import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { fmtDate } from '@/lib/dates';
import { BookOpen, Printer, ShieldCheck, Users, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TT = (l, en, es) => (l === 'es' ? es : en);

const STATUS_TONE = {
  'Signed': 'bg-emerald-50 text-emerald-700',
  'Pending': 'bg-amber-50 text-amber-700',
  'Updated Version — Acknowledgment Required': 'bg-rose-50 text-rose-700',
  'Not Yet Available — Template Pending Approval': 'bg-slate-100 text-slate-500',
};
const STATUS_ES = {
  'Signed': 'Firmado',
  'Pending': 'Pendiente',
  'Updated Version — Acknowledgment Required': 'Nueva versión — Requiere reconocimiento',
  'Not Yet Available — Template Pending Approval': 'No disponible — Plantilla pendiente de aprobación',
};
const stLabel = (s, lang) => (lang === 'es' ? (STATUS_ES[s] || s) : s);

const OpsCompanyGuidelines = () => {
  const { token } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState('document');
  const [lang, setLang] = useState('en');
  const [gl, setGl] = useState(null);
  const [glErr, setGlErr] = useState(false);
  const [roster, setRoster] = useState(null);

  useEffect(() => {
    axios.get(`${API}/team/guidelines`, auth).then((r) => { setGl(r.data.guidelines); setGlErr(false); }).catch(() => setGlErr(true));
    axios.get(`${API}/personnel/agreements/company_guidelines/roster`, auth)
      .then((r) => setRoster(r.data)).catch(() => setRoster(null));
    // eslint-disable-next-line
  }, [token]);

  const esc = (s) => String(s || '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  const printDoc = () => {
    if (!gl) return;
    const secs = (gl.sections || []).map((s) => `
      <h2 style="font-size:15px;margin:18px 0 4px;color:#0E2A47">${s.n}. ${esc(s.title[lang])}</h2>
      <div style="font-size:12px;white-space:pre-wrap;color:#333">${esc((s.body && s.body[lang]) || '')}</div>`).join('');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>${esc(gl.title[lang])} — ${TT(lang, 'Rev', 'Rev')} ${esc(gl.revision)}</title></head>
      <body style="font-family:Arial,sans-serif;max-width:720px;margin:24px auto;padding:0 16px">
      <h1 style="font-size:20px;color:#0E2A47">${esc(gl.title[lang])}</h1>
      <div style="font-size:12px;color:#777;margin-bottom:8px">${TT(lang, 'Revision', 'Revisión')} ${esc(gl.revision)}</div>
      ${secs}
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 350);
  };

  if (glErr) return (
    <div className="mx-auto max-w-md rounded-xl border border-rose-200 bg-rose-50 p-8 text-center" data-testid="ops-guidelines-error">
      <AlertTriangle className="mx-auto mb-2 text-rose-500" size={26} />
      <div className="text-sm font-semibold text-rose-700">Company Guidelines could not be loaded.</div>
      <div className="mt-1 text-xs text-slate-500">Please refresh the page or check your access.</div>
    </div>
  );
  if (!gl) return <div className="p-10 text-center text-slate-500" data-testid="ops-guidelines-loading">Loading Company Guidelines…</div>;

  const counts = (roster && roster.counts) || {};
  return (
    <div className="space-y-5" data-testid="ops-guidelines">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-[#0E2A47] p-2.5 text-white"><BookOpen size={22} /></div>
          <div>
            <h1 className="text-2xl font-extrabold text-[#0E2A47]">Company Guidelines</h1>
            <p className="text-sm text-slate-500">{gl.title.en} — Revision {gl.revision}. The same authoritative document and acknowledgment records used by TEAM.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-full ring-1 ring-slate-300" data-testid="ops-guidelines-lang">
            {['en', 'es'].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`ops-guidelines-lang-${l}`}
                className={`px-3 py-1.5 text-xs font-bold ${lang === l ? 'bg-[#0E2A47] text-white' : 'bg-white text-slate-600'}`}>{l.toUpperCase()}</button>
            ))}
          </div>
          <button onClick={printDoc} data-testid="ops-guidelines-print"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0E2A47] px-3.5 py-1.5 text-xs font-bold text-white">
            <Printer size={14} />{TT(lang, 'View / Download', 'Ver / Descargar')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[['document', TT(lang, 'Document', 'Documento')], ['acknowledgments', TT(lang, 'Acknowledgments', 'Reconocimientos')]].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ops-guidelines-tab-${k}`}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === k ? 'border-[#0E2A47] text-[#0E2A47]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {tab === 'document' && (
        <div className="space-y-4" data-testid="ops-guidelines-document">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <ShieldCheck size={13} />{TT(lang, '11 approved bilingual sections', '11 secciones bilingües aprobadas')} · Rev {gl.revision}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {(gl.sections || []).map((s) => (
              <details key={s.key} className="group p-4" data-testid={`ops-guidelines-section-${s.key}`}>
                <summary className="flex cursor-pointer items-center justify-between gap-2">
                  <span className="text-sm font-bold text-[#0E2A47]">{s.n}. {s.title[lang]}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-600">{s.status}</span>
                </summary>
                <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{(s.body && s.body[lang]) || ''}</div>
              </details>
            ))}
          </div>
        </div>
      )}

      {tab === 'acknowledgments' && (
        <div className="space-y-4" data-testid="ops-guidelines-ack">
          {!roster ? (
            <div className="p-8 text-center text-slate-400">Acknowledgment roster unavailable.</div>
          ) : (
            <>
              {counts['Not Yet Available — Template Pending Approval'] > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-slate-400" />
                  The guideline template is pending Global Administrator approval. Operations cannot bypass template approval; acknowledgment opens once approved.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[['Signed', TT(lang, 'Signed', 'Firmado')], ['Pending', TT(lang, 'Pending', 'Pendiente')], ['Updated Version — Acknowledgment Required', TT(lang, 'Re-ack needed', 'Reconfirmar')], ['Not Yet Available — Template Pending Approval', TT(lang, 'Not available', 'No disponible')]].map(([k, lbl]) => (
                  <div key={k} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ops-ack-count-${k.split(' ')[0].toLowerCase()}`}>
                    <div className="text-2xl font-extrabold text-[#0E2A47]">{counts[k] || 0}</div>
                    <div className="text-xs font-semibold text-slate-500">{lbl}</div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3">{TT(lang, 'Personnel', 'Personal')}</th>
                      <th className="px-4 py-3">{TT(lang, 'Classification', 'Clasificación')}</th>
                      <th className="px-4 py-3">{TT(lang, 'Status', 'Estado')}</th>
                      <th className="px-4 py-3">{TT(lang, 'Signed', 'Firmado')}</th>
                      <th className="px-4 py-3">{TT(lang, 'Revision', 'Revisión')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(roster.personnel || []).map((p) => (
                      <tr key={p.personnel_id} className="border-b border-slate-100 last:border-0" data-testid={`ops-ack-row-${p.personnel_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-[#0E2A47]">{p.name}</div>
                          <div className="text-xs text-slate-400">#{p.personnel_number || '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{p.worker_classification || '—'}</td>
                        <td className="px-4 py-3"><span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_TONE[p.status] || 'bg-slate-100 text-slate-600'}`}>{stLabel(p.status, lang)}</span></td>
                        <td className="px-4 py-3 text-slate-600">{p.signed_at ? fmtDate(p.signed_at) : '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{p.revision || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="flex items-center gap-1.5 text-xs text-slate-400"><Users size={13} />Acknowledgment status is read from the authoritative Personnel Master. Employees sign in TEAM; managers can request re-acknowledgment from a personnel profile but can never sign on anyone's behalf.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OpsCompanyGuidelines;
